import { Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash/fp';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    type CheckboxConsentsAndDeclarationsSettings,
    type ConditionSettings,
    LegalTextPosition,
} from '../../../api/types';
import TranslatedTextAreaField from '../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../themes/admin/Fields/FormFields';
import useConsentsAndDeclarationsOptions from '../../../utilities/useConsentsAndDeclarationsOptions';
import useDataFieldOptions from '../../../utilities/useDataFieldOptions';
import useSystemSwitchData from '../../../utilities/useSystemSwitchData';
import ConditionsDisplay from './Conditions/ConditionsDisplay';

export type CheckboxConsentFormValues = CheckboxConsentsAndDeclarationsSettings & {
    conditionSettings: ConditionSettings[];
};

export type CheckboxConsentsAndDeclarationsDetailsProps = {
    initialValues: CheckboxConsentFormValues;
    updated?: string;
    moduleName?: string;
    offset?: string;
    disabled?: boolean;
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const CheckboxConsentsAndDeclarationsDetails = ({
    updated,
    moduleName,
    offset,
    disabled = false,
}: CheckboxConsentsAndDeclarationsDetailsProps) => {
    const { t } = useTranslation('consentsAndDeclarations');
    const { values, setFieldValue } = useFormikContext<CheckboxConsentFormValues>();
    const { yesNoSwitch } = useSystemSwitchData();
    const { purposeOptions, legalTextPositionOptions } = useConsentsAndDeclarationsOptions();
    const dataFieldOptions = useDataFieldOptions();
    // Not using translated string as it depends on system's language
    const hasLegalText = !isEmpty(values.legalMarkup?.defaultValue) || !isEmpty(values.legalMarkup?.overrides);

    useEffect(() => {
        if (!values.isMandatory) {
            setFieldValue('isMandatory', false);
        }
        if (!values.legalMarkup) {
            setFieldValue('legalMarkup', { defaultValue: '', overrides: [] });
        }
        if (!values.legalTextPosition) {
            setFieldValue('legalTextPosition', LegalTextPosition.Modal);
        }
        setFieldValue('defaultChecked', undefined);
        setFieldValue('platform', undefined);
    }, [setFieldValue, values.isMandatory, values.legalMarkup, values.legalTextPosition]);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('consentsAndDeclarations:cardTitles.configuration')}>
                <Panel
                    key={t('consentsAndDeclarations:cardTitles.configuration')}
                    header={t('consentsAndDeclarations:cardTitles.configuration')}
                >
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('consentsAndDeclarations:fields.displayName', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="displayName"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('consentsAndDeclarations:fields.title', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="title"
                                required={
                                    isEmpty(values.description.defaultValue) || values.description.defaultValue === ''
                                }
                            />
                        </Col>
                        {moduleName && (
                            <Col {...colSpan}>
                                <FormFields.DisplayField
                                    {...t('consentsAndDeclarations:fields.moduleName', {
                                        returnObjects: true,
                                    })}
                                    disabled={disabled}
                                    value={moduleName}
                                />
                            </Col>
                        )}
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('consentsAndDeclarations:fields.placement', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                mode="multiple"
                                name="purpose"
                                options={purposeOptions}
                                required
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('consentsAndDeclarations:fields.dataField', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="dataField"
                                options={dataFieldOptions}
                                required
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('consentsAndDeclarations:fields.isActive', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="isActive"
                                {...yesNoSwitch}
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('consentsAndDeclarations:fields.isMandatory', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="isMandatory"
                                {...yesNoSwitch}
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputNumberField
                                {...t('consentsAndDeclarations:fields.orderNumber', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                min={1}
                                name="orderNumber"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <TranslatedTextAreaField
                                {...t('consentsAndDeclarations:fields.description', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="description"
                                required={isEmpty(values.title.defaultValue) || values.title.defaultValue === ''}
                            />
                        </Col>
                        <Col {...colSpan}>
                            <TranslatedTextAreaField
                                {...t('consentsAndDeclarations:fields.legalMarkup', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="legalMarkup"
                            />
                        </Col>
                        {hasLegalText && (
                            <Col {...colSpan}>
                                <FormFields.SelectField
                                    {...t('consentsAndDeclarations:fields.legalTextPosition', {
                                        returnObjects: true,
                                    })}
                                    defaultValue={LegalTextPosition.Modal}
                                    disabled={disabled}
                                    name="legalTextPosition"
                                    options={legalTextPositionOptions}
                                    showSearch
                                />
                            </Col>
                        )}
                        {updated && (
                            <Col {...colSpan}>
                                <FormFields.DisplayFieldWithUTCOffset
                                    {...t('common:fields.updated', { returnObjects: true, offset })}
                                    value={updated}
                                />
                            </Col>
                        )}
                    </Row>
                </Panel>
            </CollapsibleWrapper>

            <CollapsibleWrapper defaultActiveKey={t('consentsAndDeclarations:cardTitles.conditions')}>
                <Panel
                    key={t('consentsAndDeclarations:cardTitles.conditions')}
                    header={t('consentsAndDeclarations:cardTitles.conditions')}
                >
                    <ConditionsDisplay disabled={disabled} name="conditionSettings" />
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default CheckboxConsentsAndDeclarationsDetails;
